import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import {
  Search, CheckCircle, XCircle, Clock, Mail, Phone,
  Loader2, AlertCircle, UserCheck, UserX, Edit2, Trash2,
  KeyRound, Eye, EyeOff, X, NotebookPen, CalendarCheck, Calendar,
  CreditCard, Package, History, DollarSign, BookOpen, Activity
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { currencySymbol } from '../../lib/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  pending: 'bg-[#F5A623]/10 text-[#F5A623]',
  approved: 'bg-[#4CAF50]/10 text-[#4CAF50]',
  suspended: 'bg-[#D72B2B]/10 text-[#D72B2B]'
};

const statusIcons = {
  pending: Clock,
  approved: CheckCircle,
  suspended: XCircle
};

export const AdminStudents = () => {
  const [searchParams] = useSearchParams();
  const { isStrictAdmin } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    const search = searchParams.get('search');
    if (search) setSearchTerm(search);
  }, [searchParams]);

  // Edit modal
  const [editStudent, setEditStudent] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  // Password reset modal
  const [passwordStudent, setPasswordStudent] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ new_password: '', confirm: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Delete
  const [deletingId, setDeletingId] = useState(null);

  // Notes modal
  const [notesStudent, setNotesStudent] = useState(null);
  const [notesText, setNotesText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // Bulk approve
  const [approvingAll, setApprovingAll] = useState(false);

  // Assign Plan modal
  const [assignPlanStudent, setAssignPlanStudent] = useState(null);
  const [plans, setPlans] = useState([]);
  const [assignForm, setAssignForm] = useState({ plan_id: '', notes: '', approve_student: true, credits_override: '' });
  const [savingAssign, setSavingAssign] = useState(false);

  // Credits per student (active enrollment summary)
  const [creditsMap, setCreditsMap] = useState({});

  // History drawer
  const [historyStudent, setHistoryStudent] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTab, setHistoryTab] = useState('financial');

  useEffect(() => {
    fetchStudents();
    if (isStrictAdmin) {
      fetchPlans();
      fetchActiveEnrollments();
    }
  }, [isStrictAdmin]);

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/students?page_size=200`);
      setStudents(response.data.data ?? response.data);
    } catch (err) {
      console.error('Failed to fetch students:', err);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/plans?page_size=200`);
      setPlans((res.data.data ?? res.data).filter(p => p.is_active));
    } catch { /* non-blocking */ }
  };

  const fetchActiveEnrollments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/enrollments?status=active&page_size=200`);
      const list = res.data.data ?? res.data;
      const map = {};
      for (const e of list) {
        if (!map[e.student_id]) map[e.student_id] = e;
      }
      setCreditsMap(map);
    } catch { /* non-blocking */ }
  };

  const openAssignPlan = (student) => {
    if (!isStrictAdmin) {
      toast.error('Only administrators can assign plans.');
      return;
    }
    setAssignPlanStudent(student);
    setAssignForm({ plan_id: '', notes: '', approve_student: true, credits_override: '' });
  };

  const handleAssignPlan = async () => {
    if (!assignForm.plan_id) { toast.error('Select a plan'); return; }
    setSavingAssign(true);
    try {
      await axios.post(`${API_URL}/api/admin/enrollments`, {
        student_id: assignPlanStudent.id,
        plan_id: assignForm.plan_id,
        approve_student: assignForm.approve_student,
        notes: assignForm.notes || undefined,
        credits_override: assignForm.credits_override ? parseInt(assignForm.credits_override) : undefined,
      });
      toast.success('Plan assigned' + (assignForm.approve_student ? ' and student approved' : ''));
      setAssignPlanStudent(null);
      fetchStudents();
      fetchActiveEnrollments();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409) {
        // Enrollment already exists — not a failure, just inform
        toast.info(detail || 'Student already has an active enrollment.');
        setAssignPlanStudent(null);
        fetchStudents();
        fetchActiveEnrollments();
      } else {
        const msg = typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join('; ')
            : 'Failed to assign plan. Please try again.';
        toast.error(msg);
      }
    } finally {
      setSavingAssign(false);
    }
  };

  const updateStudentStatus = async (student, newStatus) => {
    const isSuspending = student.status === 'approved' && newStatus === 'suspended';
    const isReactivating = student.status === 'suspended' && newStatus === 'approved';

    if (isSuspending) {
      const confirmed = window.confirm(`Suspend ${student.name}? They will no longer be able to access the student portal.`);
      if (!confirmed) return;
    }
    if (isReactivating) {
      const confirmed = window.confirm(`Reactivate ${student.name}? This will restore their student portal access.`);
      if (!confirmed) return;
    }

    setActionLoading(student.id);
    try {
      await axios.put(`${API_URL}/api/admin/students/${student.id}/status?status=${newStatus}`);
      toast.success(`Student ${newStatus === 'approved' ? 'approved' : 'suspended'} successfully`);
      fetchStudents();
    } catch (err) {
      console.error('Failed to update student status:', err);
      toast.error('Failed to update student status');
    } finally {
      setActionLoading(null);
    }
  };

  const openEdit = (student) => {
    setEditStudent(student);
    setEditForm({ name: student.name || '', email: student.email || '', phone: student.phone || '' });
  };

  const handleEditSave = async () => {
    if (!editForm.name.trim() || !editForm.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(`${API_URL}/api/admin/students/${editStudent.id}`, {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone.trim() || null
      });
      toast.success('Student profile updated');
      setEditStudent(null);
      fetchStudents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingEdit(false);
    }
  };

  const openPasswordReset = (student) => {
    setPasswordStudent(student);
    setPasswordForm({ new_password: '', confirm: '' });
    setShowPassword(false);
  };

  const handlePasswordReset = async () => {
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setSavingPassword(true);
    try {
      await axios.put(`${API_URL}/api/admin/students/${passwordStudent.id}/password`, {
        new_password: passwordForm.new_password
      });
      toast.success(`Password reset for ${passwordStudent.name}`);
      setPasswordStudent(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSavingPassword(false);
    }
  };

  const openNotes = (student) => {
    setNotesStudent(student);
    setNotesText(student.internal_notes || '');
  };

  const openHistory = async (student) => {
    setHistoryStudent(student);
    setHistoryData(null);
    setHistoryTab('financial');
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/students/${student.id}/history`);
      setHistoryData(res.data);
    } catch {
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleTrialBooked = async (student) => {
    setActionLoading(student.id + '_trial');
    try {
      const res = await axios.put(`${API_URL}/api/admin/students/${student.id}/trial-booked`);
      setStudents(prev => prev.map(s => s.id === student.id ? { ...s, trial_booked: res.data.trial_booked } : s));
      toast.success(res.data.trial_booked ? 'Trial marked as booked' : 'Trial booking removed');
    } catch (err) {
      toast.error('Failed to update trial status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleNotesSave = async () => {
    setSavingNotes(true);
    try {
      await axios.put(`${API_URL}/api/admin/students/${notesStudent.id}/notes`, {
        internal_notes: notesText
      });
      toast.success('Notes saved');
      setNotesStudent(null);
      fetchStudents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleApproveAll = async () => {
    const pending = students.filter(s => s.status === 'pending');
    if (pending.length === 0) { toast.info('No pending students'); return; }
    const confirmed = window.confirm(`Approve all ${pending.length} pending student(s)?`);
    if (!confirmed) return;
    setApprovingAll(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/students/approve-pending`);
      toast.success(`${res.data.count} student(s) approved`);
      fetchStudents();
    } catch (err) {
      toast.error('Failed to approve all students');
    } finally {
      setApprovingAll(false);
    }
  };

  const handleDelete = async (student) => {
    const confirmed = window.confirm(
      `Permanently delete ${student.name}? This action cannot be undone and will remove all their data.`
    );
    if (!confirmed) return;
    setDeletingId(student.id);
    try {
      await axios.delete(`${API_URL}/api/admin/students/${student.id}`);
      toast.success(`${student.name} deleted`);
      fetchStudents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete student');
    } finally {
      setDeletingId(null);
    }
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch =
      student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || student.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-students-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">Students</h1>
          <p className="text-[#1A2E6E]/60 mt-1">Manage student accounts and approvals</p>
        </div>
        {isStrictAdmin && students.some(s => s.status === 'pending') && (
          <button
            onClick={handleApproveAll}
            disabled={approvingAll}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#4CAF50] text-white rounded-xl hover:bg-[#388E3C] transition-colors text-sm font-medium disabled:opacity-50 shrink-0"
          >
            {approvingAll ? <Loader2 size={16} className="animate-spin" /> : <UserCheck size={16} />}
            Approve All Pending
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A2E6E]/40" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="students-search-input"
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none transition-all"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="students-status-filter"
          className="px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none bg-white text-[#1A2E6E]"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-[#E8EAF0] p-4 text-center">
          <p className="text-2xl font-bold text-[#1A2E6E]">{students.length}</p>
          <p className="text-sm text-[#1A2E6E]/60">Total</p>
        </div>
        <div className="bg-[#F5A623]/5 rounded-xl border border-[#F5A623]/20 p-4 text-center">
          <p className="text-2xl font-bold text-[#F5A623]">
            {students.filter(s => s.status === 'pending').length}
          </p>
          <p className="text-sm text-[#1A2E6E]/60">Pending</p>
        </div>
        <div className="bg-[#4CAF50]/5 rounded-xl border border-[#4CAF50]/20 p-4 text-center">
          <p className="text-2xl font-bold text-[#4CAF50]">
            {students.filter(s => s.status === 'approved').length}
          </p>
          <p className="text-sm text-[#1A2E6E]/60">Approved</p>
        </div>
      </div>

      {/* Students List */}
      <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden">
        {filteredStudents.length > 0 ? (
          <div className="divide-y divide-[#E8EAF0]">
            {filteredStudents.map((student, index) => {
              const StatusIcon = statusIcons[student.status] || Clock;
              return (
                <motion.div
                  key={student.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-4 lg:p-6 flex flex-col lg:flex-row lg:items-center gap-4"
                  data-testid={`student-row-${student.id}`}
                >
                  {/* Avatar & Info */}
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-12 h-12 rounded-full bg-[#1A2E6E]/10 flex items-center justify-center text-[#1A2E6E] font-bold text-lg shrink-0">
                      {student.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-[#1A2E6E] truncate">{student.name}</p>
                        {student.trial_booked && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#4CAF50]/10 text-[#4CAF50] text-xs font-medium">
                            <CalendarCheck size={11} /> Trial Booked
                          </span>
                        )}
                        {creditsMap[student.id] && (() => {
                          const e = creditsMap[student.id];
                          const rem = e.credits_total - e.credits_used;
                          return (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              rem <= 1 ? 'bg-[#D72B2B]/10 text-[#D72B2B]' :
                              rem <= 3 ? 'bg-[#F5A623]/10 text-[#F5A623]' :
                                         'bg-[#4CAF50]/10 text-[#4CAF50]'
                            }`}>
                              <CreditCard size={11} /> {rem} lesson{rem !== 1 ? 's' : ''}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-[#1A2E6E]/60 mt-0.5">
                        <span className="flex items-center gap-1 truncate">
                          <Mail size={14} />
                          {student.email}
                        </span>
                        {student.phone && (
                          <span className="hidden sm:flex items-center gap-1">
                            <Phone size={14} />
                            {student.phone}
                          </span>
                        )}
                        <span className="hidden md:flex items-center gap-1">
                          <Calendar size={14} />
                          {new Date(student.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium w-fit ${statusColors[student.status]}`}>
                    <StatusIcon size={16} />
                    {student.status?.charAt(0).toUpperCase() + student.status?.slice(1)}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {isStrictAdmin && student.status === 'pending' && (
                      <button
                        onClick={() => updateStudentStatus(student, 'approved')}
                        disabled={actionLoading === student.id}
                        data-testid={`approve-student-${student.id}`}
                        className="flex items-center gap-2 px-4 py-2 bg-[#4CAF50] text-white rounded-lg hover:bg-[#388E3C] transition-colors text-sm font-medium disabled:opacity-50"
                      >
                        {actionLoading === student.id ? <Loader2 size={16} className="animate-spin" /> : <UserCheck size={16} />}
                        Approve
                      </button>
                    )}
                    {isStrictAdmin && student.status === 'approved' && (
                      <button
                        onClick={() => updateStudentStatus(student, 'suspended')}
                        disabled={actionLoading === student.id}
                        data-testid={`suspend-student-${student.id}`}
                        className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium disabled:opacity-50"
                      >
                        {actionLoading === student.id ? <Loader2 size={16} className="animate-spin" /> : <UserX size={16} />}
                        Suspend
                      </button>
                    )}
                    {isStrictAdmin && student.status === 'suspended' && (
                      <button
                        onClick={() => updateStudentStatus(student, 'approved')}
                        disabled={actionLoading === student.id}
                        data-testid={`reactivate-student-${student.id}`}
                        className="flex items-center gap-2 px-4 py-2 bg-[#1A2E6E] text-white rounded-lg hover:bg-[#2A4A8E] transition-colors text-sm font-medium disabled:opacity-50"
                      >
                        {actionLoading === student.id ? <Loader2 size={16} className="animate-spin" /> : <UserCheck size={16} />}
                        Reactivate
                      </button>
                    )}

                    {/* History — financial/audit data, strict-admin only */}
                    {isStrictAdmin && (
                      <button
                        onClick={() => openHistory(student)}
                        title="View history"
                        className="p-2 text-[#6366F1]/70 hover:text-[#6366F1] hover:bg-[#6366F1]/10 rounded-lg transition-colors"
                      >
                        <History size={16} />
                      </button>
                    )}

                    {isStrictAdmin && (
                      <button
                        onClick={() => openAssignPlan(student)}
                        title="Assign plan"
                        className="p-2 text-[#1A2E6E]/60 hover:text-[#1A2E6E] hover:bg-[#1A2E6E]/5 rounded-lg transition-colors"
                      >
                        <Package size={16} />
                      </button>
                    )}

                    {/* Toggle Trial Booked — write access, strict-admin only */}
                    {isStrictAdmin && (
                      <button
                        onClick={() => toggleTrialBooked(student)}
                        disabled={actionLoading === student.id + '_trial'}
                        title={student.trial_booked ? 'Mark trial as not booked' : 'Mark trial as booked'}
                        className={`p-2 rounded-lg transition-colors ${student.trial_booked ? 'text-[#4CAF50] hover:bg-[#4CAF50]/10' : 'text-[#1A2E6E]/40 hover:text-[#4CAF50] hover:bg-[#4CAF50]/10'}`}
                      >
                        {actionLoading === student.id + '_trial' ? <Loader2 size={16} className="animate-spin" /> : <CalendarCheck size={16} />}
                      </button>
                    )}

                    {/* Edit — write access, strict-admin only */}
                    {isStrictAdmin && (
                      <button
                        onClick={() => openEdit(student)}
                        title="Edit profile"
                        className="p-2 text-[#1A2E6E]/60 hover:text-[#1A2E6E] hover:bg-[#1A2E6E]/5 rounded-lg transition-colors"
                      >
                        <Edit2 size={16} />
                      </button>
                    )}

                    {isStrictAdmin && (
                      <button
                        onClick={() => openPasswordReset(student)}
                        title="Reset password"
                        className="p-2 text-[#F5A623]/70 hover:text-[#F5A623] hover:bg-[#F5A623]/10 rounded-lg transition-colors"
                      >
                        <KeyRound size={16} />
                      </button>
                    )}

                    {/* Notes — internal/admin-only notes, strict-admin only */}
                    {isStrictAdmin && (
                      <button
                        onClick={() => openNotes(student)}
                        title={student.internal_notes ? 'Edit internal notes' : 'Add internal notes'}
                        className={`p-2 rounded-lg transition-colors ${student.internal_notes ? 'text-[#4CAF50] hover:bg-[#4CAF50]/10' : 'text-[#1A2E6E]/40 hover:text-[#1A2E6E] hover:bg-[#1A2E6E]/5'}`}
                      >
                        <NotebookPen size={16} />
                      </button>
                    )}

                    {isStrictAdmin && (
                      <button
                        onClick={() => handleDelete(student)}
                        disabled={deletingId === student.id}
                        title="Delete student"
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {deletingId === student.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                      </button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-[#1A2E6E]/30 mx-auto mb-4" />
            <p className="text-[#1A2E6E]/60">
              {searchTerm || statusFilter !== 'all' ? 'No students match your search criteria' : 'No students registered yet'}
            </p>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {editStudent && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md"
            >
              <div className="flex items-center justify-between p-6 border-b border-[#E8EAF0]">
                <h2 className="text-lg font-bold text-[#1A2E6E]">Edit Student Profile</h2>
                <button onClick={() => setEditStudent(null)} className="p-2 hover:bg-[#1A2E6E]/5 rounded-lg">
                  <X size={20} className="text-[#1A2E6E]/60" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Full Name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Phone <span className="text-[#1A2E6E]/40">(optional)</span></label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                  />
                </div>
              </div>
              <div className="flex gap-3 p-6 pt-0">
                <button
                  onClick={() => setEditStudent(null)}
                  className="flex-1 py-3 rounded-xl border border-[#E8EAF0] text-[#1A2E6E] hover:bg-[#1A2E6E]/5 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSave}
                  disabled={savingEdit}
                  className="flex-1 py-3 rounded-xl bg-[#1A2E6E] text-white hover:bg-[#16275C] transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingEdit ? <Loader2 size={16} className="animate-spin" /> : null}
                  Save Changes
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Notes Modal */}
      <AnimatePresence>
        {notesStudent && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md"
            >
              <div className="flex items-center justify-between p-6 border-b border-[#E8EAF0]">
                <div>
                  <h2 className="text-lg font-bold text-[#1A2E6E]">Internal Notes</h2>
                  <p className="text-sm text-[#1A2E6E]/60 mt-0.5">{notesStudent.name} — visible only to admins</p>
                </div>
                <button onClick={() => setNotesStudent(null)} className="p-2 hover:bg-[#1A2E6E]/5 rounded-lg">
                  <X size={20} className="text-[#1A2E6E]/60" />
                </button>
              </div>
              <div className="p-6">
                <textarea
                  rows={6}
                  value={notesText}
                  onChange={e => setNotesText(e.target.value)}
                  placeholder="English level, learning goals, important observations, payment notes..."
                  className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none resize-none text-sm"
                />
              </div>
              <div className="flex gap-3 p-6 pt-0">
                <button
                  onClick={() => setNotesStudent(null)}
                  className="flex-1 py-3 rounded-xl border border-[#E8EAF0] text-[#1A2E6E] hover:bg-[#1A2E6E]/5 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleNotesSave}
                  disabled={savingNotes}
                  className="flex-1 py-3 rounded-xl bg-[#4CAF50] text-white hover:bg-[#388E3C] transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingNotes ? <Loader2 size={16} className="animate-spin" /> : null}
                  Save Notes
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Assign Plan Modal */}
      <AnimatePresence>
        {assignPlanStudent && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md"
            >
              <div className="flex items-center justify-between p-6 border-b border-[#E8EAF0]">
                <div>
                  <h2 className="text-lg font-bold text-[#1A2E6E]">Assign Plan</h2>
                  <p className="text-sm text-[#1A2E6E]/60 mt-0.5">{assignPlanStudent.name}</p>
                </div>
                <button onClick={() => setAssignPlanStudent(null)} className="p-2 hover:bg-[#1A2E6E]/5 rounded-lg">
                  <X size={20} className="text-[#1A2E6E]/60" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Plan *</label>
                  <select
                    value={assignForm.plan_id}
                    onChange={e => setAssignForm(f => ({ ...f, plan_id: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none bg-white"
                  >
                    <option value="">— select a plan —</option>
                    {plans.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} — {p.credits} lessons — {currencySymbol(p.currency)}{p.price_brl}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">
                    Credits override <span className="text-[#1A2E6E]/40 font-normal">(leave blank to use plan default)</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={assignForm.credits_override}
                    onChange={e => setAssignForm(f => ({ ...f, credits_override: e.target.value }))}
                    placeholder="e.g. 10"
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Notes <span className="text-[#1A2E6E]/40 font-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={assignForm.notes}
                    onChange={e => setAssignForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="e.g. PIX received, courtesy class"
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                  />
                </div>
                <label className="flex items-center gap-3 cursor-pointer p-3 bg-[#F8F9FC] rounded-xl border border-[#E8EAF0]">
                  <input
                    type="checkbox"
                    checked={assignForm.approve_student}
                    onChange={e => setAssignForm(f => ({ ...f, approve_student: e.target.checked }))}
                    className="w-4 h-4 accent-[#1A2E6E]"
                  />
                  <div>
                    <p className="text-sm font-medium text-[#1A2E6E]">Approve student automatically</p>
                    <p className="text-xs text-[#1A2E6E]/50">Only applies if student is currently pending</p>
                  </div>
                </label>
              </div>
              <div className="flex gap-3 p-6 pt-0">
                <button
                  onClick={() => setAssignPlanStudent(null)}
                  className="flex-1 py-3 rounded-xl border border-[#E8EAF0] text-[#1A2E6E] hover:bg-[#1A2E6E]/5 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignPlan}
                  disabled={savingAssign}
                  className="flex-1 py-3 rounded-xl bg-[#4CAF50] text-white hover:bg-[#388E3C] transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingAssign ? <Loader2 size={16} className="animate-spin" /> : <Package size={16} />}
                  Assign Plan
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Reset Password Modal */}
      <AnimatePresence>
        {passwordStudent && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md"
            >
              <div className="flex items-center justify-between p-6 border-b border-[#E8EAF0]">
                <div>
                  <h2 className="text-lg font-bold text-[#1A2E6E]">Reset Password</h2>
                  <p className="text-sm text-[#1A2E6E]/60 mt-0.5">{passwordStudent.name}</p>
                </div>
                <button onClick={() => setPasswordStudent(null)} className="p-2 hover:bg-[#1A2E6E]/5 rounded-lg">
                  <X size={20} className="text-[#1A2E6E]/60" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">New Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={passwordForm.new_password}
                      onChange={e => setPasswordForm(f => ({ ...f, new_password: e.target.value }))}
                      placeholder="At least 8 characters"
                      className="w-full px-4 py-3 pr-12 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1A2E6E]/40 hover:text-[#1A2E6E]"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Confirm Password</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={passwordForm.confirm}
                    onChange={e => setPasswordForm(f => ({ ...f, confirm: e.target.value }))}
                    placeholder="Repeat the password"
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                  />
                </div>
              </div>
              <div className="flex gap-3 p-6 pt-0">
                <button
                  onClick={() => setPasswordStudent(null)}
                  className="flex-1 py-3 rounded-xl border border-[#E8EAF0] text-[#1A2E6E] hover:bg-[#1A2E6E]/5 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasswordReset}
                  disabled={savingPassword}
                  className="flex-1 py-3 rounded-xl bg-[#F5A623] text-white hover:bg-[#E09000] transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingPassword ? <Loader2 size={16} className="animate-spin" /> : null}
                  Reset Password
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── History Drawer ─────────────────────────────────────────────────── */}
      <AnimatePresence>
        {historyStudent && (
          <div className="fixed inset-0 z-50 flex">
            {/* Backdrop */}
            <div className="flex-1 bg-black/40" onClick={() => setHistoryStudent(null)} />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
              className="w-full max-w-xl bg-white shadow-2xl flex flex-col h-full overflow-hidden"
            >
              {/* Drawer header */}
              <div className="flex items-center justify-between p-5 border-b border-[#E8EAF0] shrink-0">
                <div>
                  <h2 className="text-lg font-bold text-[#1A2E6E]">{historyStudent.name}</h2>
                  <p className="text-xs text-[#1A2E6E]/50 mt-0.5">{historyStudent.email}</p>
                </div>
                <button onClick={() => setHistoryStudent(null)} className="p-2 hover:bg-[#1A2E6E]/5 rounded-lg">
                  <X size={20} className="text-[#1A2E6E]/60" />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-[#E8EAF0] shrink-0">
                {[
                  { id: 'financial', label: 'Financial', icon: DollarSign },
                  { id: 'classes',   label: 'Classes',   icon: BookOpen },
                  { id: 'log',       label: 'Log',        icon: Activity },
                ].map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setHistoryTab(id)}
                    className={`flex items-center gap-1.5 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                      historyTab === id
                        ? 'border-[#1A2E6E] text-[#1A2E6E]'
                        : 'border-transparent text-[#1A2E6E]/50 hover:text-[#1A2E6E]'
                    }`}
                  >
                    <Icon size={14} /> {label}
                  </button>
                ))}
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-5">
                {historyLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
                  </div>
                ) : historyData ? (
                  <>
                    {/* Financial Tab */}
                    {historyTab === 'financial' && (
                      <div className="space-y-5">
                        <h3 className="text-sm font-semibold text-[#1A2E6E]/60 uppercase tracking-wide">Enrollments</h3>
                        {historyData.enrollments.length === 0 ? (
                          <p className="text-sm text-[#1A2E6E]/40">No enrollments yet.</p>
                        ) : (
                          <div className="space-y-2">
                            {historyData.enrollments.map(e => (
                              <div key={e.id} className="p-4 rounded-xl border border-[#E8EAF0] text-sm">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium text-[#1A2E6E]">{e.plan_name || e.plan_id}</span>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    e.status === 'active' ? 'bg-[#4CAF50]/10 text-[#4CAF50]' :
                                    e.status === 'expired' ? 'bg-[#D72B2B]/10 text-[#D72B2B]' :
                                    'bg-[#1A2E6E]/10 text-[#1A2E6E]'
                                  }`}>{e.status}</span>
                                </div>
                                <div className="flex gap-4 text-xs text-[#1A2E6E]/50">
                                  <span>{e.credits_remaining}/{e.credits_total} lessons left</span>
                                  {e.expires_at && <span>Expires {new Date(e.expires_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <h3 className="text-sm font-semibold text-[#1A2E6E]/60 uppercase tracking-wide pt-2">Payments</h3>
                        {historyData.payments.length === 0 ? (
                          <p className="text-sm text-[#1A2E6E]/40">No payments recorded.</p>
                        ) : (
                          <div className="space-y-2">
                            {historyData.payments.map(p => (
                              <div key={p.id} className="flex items-center justify-between p-4 rounded-xl border border-[#E8EAF0] text-sm">
                                <div>
                                  <p className="font-medium text-[#1A2E6E]">{currencySymbol(p.currency)}{Number(p.amount).toFixed(2)}</p>
                                  <p className="text-xs text-[#1A2E6E]/50 mt-0.5">
                                    {p.method?.toUpperCase()} · {p.paid_at ? new Date(p.paid_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                  </p>
                                </div>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  p.status === 'completed' ? 'bg-[#4CAF50]/10 text-[#4CAF50]' :
                                  p.status === 'failed' ? 'bg-[#D72B2B]/10 text-[#D72B2B]' :
                                  'bg-[#F5A623]/10 text-[#F5A623]'
                                }`}>{p.status}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Classes Tab */}
                    {historyTab === 'classes' && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-[#1A2E6E]/5 rounded-xl p-4 text-center">
                            <p className="text-2xl font-bold text-[#1A2E6E]">{historyData.classes_completed}</p>
                            <p className="text-xs text-[#1A2E6E]/60 mt-0.5">Completed</p>
                          </div>
                          <div className="bg-[#F5A623]/8 rounded-xl p-4 text-center">
                            <p className="text-2xl font-bold text-[#F5A623]">{historyData.classes.filter(c => c.status === 'scheduled').length}</p>
                            <p className="text-xs text-[#1A2E6E]/60 mt-0.5">Upcoming</p>
                          </div>
                        </div>
                        {historyData.classes.length === 0 ? (
                          <p className="text-sm text-[#1A2E6E]/40">No classes yet.</p>
                        ) : (
                          <div className="space-y-1.5">
                            {historyData.classes.map(c => (
                              <div key={c.id} className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#E8EAF0] text-sm">
                                <div>
                                  <p className="font-medium text-[#1A2E6E]">{c.title}</p>
                                  {c.scheduled_at && (
                                    <p className="text-xs text-[#1A2E6E]/50 mt-0.5">
                                      {new Date(c.scheduled_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                    </p>
                                  )}
                                </div>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  c.status === 'completed' ? 'bg-[#4CAF50]/10 text-[#4CAF50]' :
                                  c.status === 'scheduled' ? 'bg-[#1A2E6E]/10 text-[#1A2E6E]' :
                                  'bg-[#F5A623]/10 text-[#F5A623]'
                                }`}>{c.status}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Log Tab */}
                    {historyTab === 'log' && (
                      <div className="space-y-2">
                        {historyData.audit_log.length === 0 ? (
                          <p className="text-sm text-[#1A2E6E]/40">No audit events recorded.</p>
                        ) : (
                          historyData.audit_log.map(entry => (
                            <div key={entry.id} className="flex gap-3 p-3 rounded-xl border border-[#E8EAF0] text-xs">
                              <div className="w-2 h-2 rounded-full bg-[#1A2E6E]/30 mt-1.5 shrink-0" />
                              <div className="min-w-0">
                                <p className="font-medium text-[#1A2E6E] font-mono">{entry.action}</p>
                                {entry.metadata && Object.keys(entry.metadata).length > 0 && (
                                  <p className="text-[#1A2E6E]/50 mt-0.5 truncate">
                                    {Object.entries(entry.metadata).filter(([k]) => !['student_id'].includes(k)).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                                  </p>
                                )}
                                <p className="text-[#1A2E6E]/30 mt-0.5">
                                  {new Date(entry.created_at).toLocaleString('en-GB')} · by {entry.actor_id === 'system' || entry.actor_id === 'stripe_webhook' ? entry.actor_id : 'admin'}
                                </p>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminStudents;
